import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { CreateProjectDialog } from "@/components/CreateProjectDialog";
import { StageBadge, StatusBadge, STAGE_LABELS, nextAction } from "@/lib/projectMeta";
import { Button } from "@/components/ui/button";
import { Film, ArrowRight, AlertTriangle } from "lucide-react";

export default function Pipeline() {
  const { selectedChannelId } = useStudio();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/projects/pipeline${selectedChannelId ? `?channel_id=${selectedChannelId}` : ""}`);
      setData(data);
    } catch (e) { setError(apiError(e)); }
  }, [selectedChannelId]);
  useEffect(() => { load(); }, [load]);

  const visibleStages = useMemo(
    () => (data ? data.stages.filter((s) => data.grouped[s]?.length) : []),
    [data],
  );

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState label="Loading pipeline…" />;

  const total = Object.values(data.grouped).reduce((a, b) => a + b.length, 0);

  return (
    <div className="animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Production</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Content Pipeline</h1>
          <p className="text-sm text-slate-400 mt-2">Every video from idea to approved. A failure in one never blocks others.</p>
        </div>
        <CreateProjectDialog trigger={<Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="pipeline-new-video">+ New video</Button>} />
      </div>

      {total === 0 ? (
        <EmptyState icon={Film} title="No projects yet" description="Create your first video project to start the production pipeline."
          action={<CreateProjectDialog trigger={<Button className="bg-violet-600 hover:bg-violet-500">Create project</Button>} />} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {visibleStages.map((stage) => (
            <div key={stage} className="bg-[#0d1526] border border-slate-800/70 rounded-xl p-4" data-testid={`stage-col-${stage}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-display font-semibold text-slate-200">{STAGE_LABELS[stage]}</h3>
                <span className="text-xs text-slate-500">{data.grouped[stage].length}</span>
              </div>
              <div className="space-y-3">
                {data.grouped[stage].map((p) => (
                  <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`pipeline-project-${p.id}`}
                    className="w-full text-left bg-[#111827] border border-slate-800 rounded-lg p-3.5 hover:border-violet-500/40 transition-colors group">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-100 truncate">{p.title}</span>
                      <StatusBadge status={p.stage_status} />
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{p.channel_name} · {p.content_language} · {p.scene_count} scenes</p>
                    {p.stage_status === "failed" && p.stage === "render" && (
                      <p className="text-xs text-rose-400 flex items-center gap-1 mb-2"><AlertTriangle className="w-3 h-3" /> Render failed — retryable</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-violet-300">{nextAction(p)}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">${(p.cost_actual || 0).toFixed(4)} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
